/**
 * Lightweight API client for custom auth service endpoints.
 * Uses Better Auth session cookies for authentication (same origin as authClient).
 */

import { authClient } from '@/config/auth-client';
import { ENV } from '@/config/env';

const AUTH_BASE_URL = ENV.AUTH_URL;

class AuthApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

async function getSessionToken(): Promise<string | null> {
  try {
    const session = await authClient.getSession();
    return session?.data?.session?.token || null;
  } catch {
    return null;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await getSessionToken();
  if (!token) {
    throw new AuthApiError(401, 'No session token available');
  }

  const url = `${AUTH_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 204) {
    return {} as T;
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const message = data?.message || data?.error || `Request failed (${response.status})`;
    throw new AuthApiError(response.status, message);
  }

  return data as T;
}

export const authApiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

export { AuthApiError };
